// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { OrganizationUser } from '@/account/api/types';
import { RegisteredContacts } from '@/account/components/registered-contacts';
import { formatSubscriptionExpirationNotice } from '@/account/utilities/format-subscription-expiration-notice';
import {
  InfoOutlined,
  ReportOutlined,
} from '@/constants/hardfin-standard-icons';
import { Alert, IconButton, Tooltip } from '@mui/material';
import { DateTime } from 'luxon';

interface Props {
  /**
   * The name of the organization whose subscription expired
   */
  organizationName: string;

  /**
   * The last day of the organization's subscription
   */
  subscriptionEndDate: DateTime;

  /**
   * The first day the organization loses access, when one has been set
   */
  accessEndDate: DateTime | null;

  /**
   * The member registered as the contract contact, when one has been recorded
   */
  contractContact: OrganizationUser | null;

  /**
   * The member registered as the billing contact, when one has been recorded
   */
  billingContact: OrganizationUser | null;
}

/**
 * A full-width banner that sits above the navigation bar while an
 * organization's subscription has expired but access has not yet ended. The
 * sentence ends with a small info icon whose tooltip names the contacts
 * Hardfin has registered for the organization.
 */
export function SubscriptionExpirationBanner(props: Props) {
  return (
    <Alert severity="warning" square icon={<ReportOutlined />}>
      {formatSubscriptionExpirationNotice(
        props.organizationName,
        props.subscriptionEndDate,
        props.accessEndDate
      )}
      <Tooltip
        arrow
        title={
          <RegisteredContacts
            contractContact={props.contractContact}
            billingContact={props.billingContact}
            surface="dark"
          />
        }
      >
        <IconButton
          aria-label="Registered contacts"
          color="inherit"
          size="small"
          sx={{
            ml: 0.5,
            p: 0,
            fontSize: 'inherit',
            verticalAlign: 'text-bottom',
          }}
        >
          <InfoOutlined fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Alert>
  );
}
